import { useRef, useContext } from 'react'
import { useTranslation } from 'react-i18next'
import getNameOfGqlQuery from 'helpers/getNameOfGqlQuery'
import { ConfirmModal } from 'components/Modal'
import { useMutation, StatusIndicatorContext } from 'components/graphql'
import get from 'lodash/get'
import isEqual from 'lodash/isEqual'

const useMutations = props => {
	const { history, section, subsectionData: { updateMutation, refetchQueries } } = props
	const { deleteMutation, createMutation, typename, requiredFields = [], listQuery } = props.sectionData
	const { handleNewRecord, copyId } = get(props, 'addNewRecord', {})

	const listQueryRef = useRef()
	const { addPromise } = useContext(StatusIndicatorContext)
	const [t] = useTranslation()

	/* MAIN HANDLER */
	const handleMutation = async ({ onError, ...mutationParams }) => {
		const { client } = props
		const res = client.mutate(mutationParams)
		addPromise(res)
		return res
	}

	/* UPDATE RECORD */
	const [updateItem] = useMutation(updateMutation)
	const handleUpdateItem = (recordId, { formData }) => async (fields, options = {}) => {
		const newFields = Object.keys(fields).reduce((acc, key) => {
			if(!isEqual(formData[key], fields[key])) {
				acc[key] = fields[key]
			}
			return acc
		}, {})

		const { isActive, handleMultiEdit } = props.multiEdit
		if(isActive) {
			return handleMultiEdit({ fields: newFields })
		}

		return updateItem({
			mutation: updateMutation,
			variables: { id: recordId, fields: newFields },
			refetchQueries,
			...options
		})
	}

	/* DELETE RECORD */
	const [deleteItem] = useMutation(deleteMutation)
	const handleDeleteItem = id => {
		const mutationField = getNameOfGqlQuery(deleteMutation)
		history.push(`/${section}`)
		return deleteItem({
			mutation: deleteMutation,
			variables: { id },
			refetchQueries: [{
				query: listQuery,
				variables: get(listQueryRef, 'current.variables', {})
			}],
			optimisticResponse: {
				[mutationField]: {
					id,
					__typename: typename
				}
			}
		})
	}
	const safeDeleteItem = id => ConfirmModal({
		text: t('common.strings.sure_delete'),
		label: t('common.strings.delete_data')
	}).then(
		confirmed => handleDeleteItem(id)
	).catch(e => {})

	/* ADD NEW RECORD */
	const [createItem] = useMutation(createMutation)
	const handleCreateItem = (fields, options = {}) => {
		let required = {}
		requiredFields.forEach(field => {
			const value = fields[field]
			required[field] = value
			delete fields[field]
			if(!value) {
				throw new Error('Required field is empty')
			}
		})
		return createItem({
			mutation: createMutation,
			variables: { required, fields, ...copyId && { copyId } },
			update: (cache, { data }) => {
				const { createMutation: response } = data
				handleNewRecord(response.id)
			},
			...options && { ...options }
		})
	}

	let mutations = {
		...updateItem && { updateItem: handleUpdateItem },
		...createItem && { createItem: handleCreateItem },
		...deleteItem && { deleteItem: safeDeleteItem }
	}
	return {
		mutations,
		handleMutation: handleMutation,
		listQueryRef: listQueryRef
	}
}

export default useMutations
